import { atividadeSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Campo, Checkbox, Input } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/atividades')({ component: Atividades })

type Item = NonNullable<ReturnType<typeof useAtividades>['data']>[number]
function useAtividades() {
  const trpc = useTRPC()
  return useQuery(trpc.cadastros.atividades.listar.queryOptions())
}

function Atividades() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useAtividades()
  const g = useGavetaCadastro<Item>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.atividades.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.atividades.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.atividades.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: atividadeSchema,
    numericos: ['ordem'],
    booleanos: ['ativo', 'contaComoProdutivo'],
    setErros: g.setErros,
    sucesso: (d) => `Atividade ${d.nome} salva.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <>
      <BarraTitulo
        titulo="Atividades sem ficha"
        descricao="O que o operador faz fora das fichas (limpeza, empilhadeira, descarregar caminhão). Ele escolhe no tablet, inicia e encerra. Conta como tempo trabalhado e não mexe na eficiência."
        onNovo={g.abrirNovo}
        rotuloNovo="Nova atividade"
      />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Tempo trabalhado</Th>
          <Th>Ordem</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((a) => (
            <Linha key={a.id}>
              <Td className="font-medium">
                {a.nome} {!a.ativo && <Ativo ativo={false} />}
              </Td>
              <Td>{a.contaComoProdutivo ? 'Conta' : 'Não conta'}</Td>
              <Td numerico>{a.ordem}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={a.nome} onClick={() => g.abrirEditar(a)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={4} />}
          {lista.data?.length === 0 && <Vazio colunas={4}>Nenhuma atividade cadastrada.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar atividade' : 'Nova atividade'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-atividade"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario
          id="form-atividade"
          onSubmit={submit}
          erros={g.erros}
          onLimparErro={g.limparErro}
        >
          <Campo label="Nome (no botão do tablet)" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo label="Ordem no tablet" erro={g.erros.ordem}>
            {(p) => (
              <Input {...p} name="ordem" type="number" min={0} defaultValue={g.item?.ordem ?? 0} />
            )}
          </Campo>
          <Checkbox
            name="contaComoProdutivo"
            label="Conta como tempo trabalhado (produtividade)"
            defaultChecked={g.item?.contaComoProdutivo ?? true}
          />
          <Checkbox name="ativo" label="Ativa" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </>
  )
}
